<template>
  <div class="liff-page-array-editor">
    <!-- 頁面卡片列表 -->
    <div
      v-for="(page, index) in pageItems"
      :key="page.pageKey"
      class="page-item mb-4"
    >
      <!-- 卡片標頭 -->
      <div class="page-header d-flex align-items-center mb-3">
        <span class="page-order badge badge-primary mr-2">{{ index + 1 }}</span>
        <span class="page-key-label flex-grow-1">{{ page.pageKey }}</span>
        <div class="page-actions d-flex flex-shrink-0 ml-3">
          <b-button
            variant="outline-danger"
            size="sm"
            @click="confirmRemovePage(page.pageKey)"
            title="刪除此頁面"
          >
            <i class="mdi mdi-delete" />
          </b-button>
        </div>
      </div>

      <!-- 一般欄位區 -->
      <div class="page-meta mb-3">
        <b-form-row>
          <b-col
            v-for="field in page.metaFields"
            :key="field.key"
            cols="12"
            class="mb-2"
          >
            <b-form-group
              label-cols="12"
              label-cols-lg="3"
              label-size="sm"
              :label="field.label"
              label-class="page-meta-label py-1"
              class="mb-0"
            >
              <b-form-checkbox
                v-if="field.type === 'boolean'"
                v-model="form[fullKey(page.pageKey, field.key)]"
                :value="true"
                :unchecked-value="false"
                class="pt-1"
              />
              <b-form-input
                v-else
                v-model="form[fullKey(page.pageKey, field.key)]"
                size="sm"
                :placeholder="field.placeholder || ''"
              />
            </b-form-group>
          </b-col>
        </b-form-row>
      </div>

      <!-- 頁面內容 (CKEditor) -->
      <div class="page-content">
        <div class="page-meta-label mb-1">頁面內容</div>
        <ckeditor
          :editor="editor"
          :config="editorConfig"
          v-model="form[fullKey(page.pageKey, 'content')]"
        />
      </div>
    </div>

    <!-- 新增頁面按鈕 -->
    <div class="add-page-btn mt-3">
      <b-button variant="outline-primary" @click="openAddModal">
        <i class="mdi mdi-plus mr-1" /> 新增頁面
      </b-button>
    </div>

    <!-- 新增頁面 Modal -->
    <b-modal
      id="add-liff-page-modal"
      title="新增頁面"
      :ok-disabled="!newPageKey || newPageKeyDuplicated"
      ok-title="確認新增"
      cancel-title="取消"
      @ok="confirmAddPage"
      @hidden="resetNewPageKey"
    >
      <b-form-group
        label="頁面識別代號 (path_key)"
        label-size="sm"
        description="僅允許英文小寫、數字與底線，例如：event_page。建立後無法更改。"
        :state="newPageKeyState"
        :invalid-feedback="newPageKeyError"
      >
        <b-form-input
          v-model="newPageKey"
          placeholder="例如：advocate_page"
          :state="newPageKeyState"
          autofocus
        />
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

// 固定的欄位定義（不含 content，content 單獨以 CKEditor 呈現）
const META_FIELD_DEFS = [
  { key: "enable", label: "是否啟用", type: "boolean" },
  { key: "browser_title", label: "瀏覽器標題", type: "string", placeholder: "顯示在瀏覽器分頁的標題" },
  { key: "form_title", label: "頁面標題", type: "string", placeholder: "頁面主要標題" },
  { key: "form_desc", label: "副標題", type: "string", placeholder: "頁面副標題或說明" },
  { key: "button_text", label: "按鈕文字", type: "string", placeholder: "例如：立即參加" },
  { key: "button_url", label: "按鈕網址", type: "string", placeholder: "例如：https://..." },
  { key: "button_action", label: "按鈕動作", type: "string", placeholder: "例如：URL" },
  { key: "enable_page_title", label: "是否顯示標題", type: "boolean" },
  { key: "enable_menu_items", label: "是否啟用選單", type: "boolean" },
  { key: "enable_page_image", label: "是否顯示表頭主圖", type: "boolean" },
  { key: "enable_page_kv_image", label: "是否顯示 KV 圖片", type: "boolean" },
];

// 新增頁面時的預設欄位值
const DEFAULT_PAGE_VALUES = {
  enable: true,
  browser_title: "",
  form_title: "",
  form_desc: "",
  content: "",
  button_text: "",
  button_url: "",
  button_action: "URL",
  enable_page_title: false,
  enable_menu_items: false,
  enable_page_image: false,
  enable_page_kv_image: false,
};

export default {
  name: "LiffPageConfigTreeEditor",
  props: {
    /**
     * 模組代號，例如 'liff_page'
     */
    moduleCode: {
      type: String,
      default: "liff_page",
    },
    /**
     * page_config_tree 物件，key 為 pageKey
     */
    pageConfigTree: {
      type: Object,
      default: () => ({}),
    },
    /**
     * 父層的 reactive form 物件，直接雙向讀寫
     */
    form: {
      type: Object,
      required: true,
    },
  },
  emits: ["add-page", "remove-page"],
  data() {
    return {
      editor: ClassicEditor,
      editorConfig: {
        toolbar: {
          items: [
            "heading", "|",
            "bold", "italic", "link", "|",
            "bulletedList", "numberedList", "|",
            "blockQuote", "insertTable", "|",
            "undo", "redo",
          ],
        },
        language: "zh",
      },
      // 內部維護的頁面排序陣列（只存 pageKey）
      pageOrder: [],
      // 新增 Modal 的輸入值
      newPageKey: "",
    };
  },
  computed: {
    /**
     * 將 pageConfigTree Object 轉為有序的 Array，供 v-for 使用
     */
    pageItems() {
      return this.pageOrder
        .filter((key) => this.pageConfigTree && this.pageConfigTree[key] !== undefined)
        .map((pageKey) => ({
          pageKey,
          metaFields: META_FIELD_DEFS,
        }));
    },
    /**
     * 判斷 path_key 是否合法（英數底線）
     */
    newPageKeyValid() {
      return /^[a-z0-9_]+$/.test(this.newPageKey);
    },
    /**
     * 判斷 path_key 是否已經存在
     */
    newPageKeyDuplicated() {
      return this.pageOrder.includes(this.newPageKey);
    },
    /**
     * b-form-group :state 值
     */
    newPageKeyState() {
      if (!this.newPageKey) return null;
      return this.newPageKeyValid && !this.newPageKeyDuplicated ? true : false;
    },
    /**
     * b-form-group invalid-feedback 文字
     */
    newPageKeyError() {
      if (!this.newPageKey) return "";
      if (this.newPageKeyDuplicated) return "此頁面識別代號已存在";
      if (!this.newPageKeyValid) return "僅允許英文小寫、數字與底線";
      return "";
    },
  },
  watch: {
    /**
     * 監聽 pageConfigTree 變動，同步內部排序陣列
     */
    pageConfigTree: {
      immediate: true,
      handler(newTree) {
        if (!newTree) {
          this.pageOrder = [];
          return;
        }
        const existingKeys = new Set(this.pageOrder);
        const treeKeys = Object.keys(newTree);
        // 移除已不存在的 key
        this.pageOrder = this.pageOrder.filter((k) => treeKeys.includes(k));
        // 補上新增的 key（尾端追加）
        treeKeys.forEach((k) => {
          if (!existingKeys.has(k)) {
            this.pageOrder.push(k);
          }
        });
      },
    },
  },
  methods: {
    /**
     * 組合完整的 form key
     * @param {string} pageKey
     * @param {string} fieldKey
     * @returns {string}  例：liff_page.fun_page.enable
     */
    fullKey(pageKey, fieldKey) {
      return `${this.moduleCode}.${pageKey}.${fieldKey}`;
    },

    /**
     * 開啟新增頁面 Modal
     */
    openAddModal() {
      this.newPageKey = "";
      this.$bvModal.show("add-liff-page-modal");
    },

    /**
     * 重置 Modal 輸入值
     */
    resetNewPageKey() {
      this.newPageKey = "";
    },

    /**
     * 確認新增頁面，Emit 給父層處理
     */
    confirmAddPage() {
      if (!this.newPageKey || !this.newPageKeyValid || this.newPageKeyDuplicated) {
        return;
      }
      this.$emit("add-page", this.newPageKey, { ...DEFAULT_PAGE_VALUES });
    },

    /**
     * 刪除頁面確認（swal）
     * @param {string} pageKey
     */
    confirmRemovePage(pageKey) {
      this.$swal
        .fire({
          title: `確定要刪除「${pageKey}」嗎？`,
          text: "刪除後此頁面所有設定將無法復原",
          type: "warning",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
          confirmButtonText: "確定刪除",
          cancelButtonText: "取消",
        })
        .then((result) => {
          if (result.value || result.isConfirmed) {
            this.$emit("remove-page", pageKey);
          }
        });
    },

  },
};
</script>

<style scoped lang="scss">
.liff-page-array-editor {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1.25rem;
  background-color: #fafafa;
}

.page-item {
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.page-order {
  font-size: 0.9rem;
  min-width: 28px;
  text-align: center;
}

.page-key-label {
  font-weight: 700;
  font-size: 0.95rem;
  color: #2f3b52;
  letter-spacing: 0.02em;
}

.page-meta {
  padding: 0.75rem;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
}

.page-meta-label {
  margin-bottom: 0.25rem;
  color: #6c757d;
  font-weight: 600;
  font-size: 0.85rem;
}

.page-content {
  margin-top: 0.75rem;
}

.page-content::v-deep .ck-editor__editable {
  min-height: 150px;
  max-height: 400px;
  overflow-y: auto;

  p, h1, h2, h3, h4, h5, h6 {
    margin-bottom: 0.35rem;
    margin-top: 0;
  }

  h1 { font-size: 1.5rem; }
  h2 { font-size: 1.4rem; }
  h3 { font-size: 1.3rem; }
  h4 { font-size: 1.2rem; }
  h5 { font-size: 1.1rem; }
  h6 { font-size: 1rem; }

  a {
    color: #0066cc !important;
    text-decoration: underline !important;
  }
}

.add-page-btn {
  text-align: center;
}
</style>
